import { string } from 'prop-types';
import React from 'react';
import { useDispatch } from 'react-redux';

import CommonImage from '@components/CommonImage';
import LoaderComponent from '@components/LoaderComponent';

import useTrendResearchFilterParams from '@features/trend-research/hooks/useTrendResearchFilterParams';
import useKeyDesigns from '@features/trend-research/hooks/useTrendResearchKeyDesigns';
import EmptyState from '@features/trend-research/components/common/EmptyState';
import {
	Card,
	CardContent,
	PrintGraphicsImageContainer,
	TopBar
} from '@features/trend-research/components/common/TrendCard';
import { filterFieldType } from '@features/filter/static/filterUtil';

import { useInsightSummary, useSelectedGroup } from '@store/reducers/trend-research/TrendResearchReducer';
import { storeItemInGlobalFilter } from '@store/actions/FilterAction';
import { setTrendResearchSelectedGroup } from '@store/actions/trend-research/TrendResearchAction';

import { trackMixPanelEvent } from '@services/commonService';

import { processDecimalValue } from '@utils/Util';

const KeyGraphicsOrPrint = ({ title, keyType, summaryField, source, type }) => {
	const summaryCounts = useInsightSummary();
	const dispatch = useDispatch();
	const removeParamList =
		source === 'Instagram' ? ['fabricTypeList', 'brandCodeList'] : ['instagramUserNameList'];
	const { requestBody } = useTrendResearchFilterParams(removeParamList);
	const selectedGroup = useSelectedGroup();
	const { designs, isLoading } = useKeyDesigns(keyType, requestBody, summaryField);

	const renderCards = () => {
		let key = 'TREND_GRAPHICS';
		const handleRoute = (item) => {
			trackMixPanelEvent(
				{
					cardName: item.graphicsName,
					groupType: selectedGroup
				},
				'TRENDS_GROUPED_CARD_ON_CLICK'
			);
			dispatch(
				storeItemInGlobalFilter({
					name: filterFieldType[key]['idName'],
					key: item.graphicsName,
					value: item.graphicsName,
					identifier: `${filterFieldType[key]['idName']}_${item.graphicsName}`,
					checked: true
				})
			);
			dispatch(setTrendResearchSelectedGroup('DEFAULT'));
		};

		return designs?.map((item, idx) => {
			const [mainImage, ...images] = item?.imageUrlList || [];
			const contents = {
				title: item.graphicsName,
				itemCount: item.graphicsCount,
				percentage: summaryCounts?.noOfProducts
					? processDecimalValue((item.graphicsCount / summaryCounts.noOfProducts) * 100) + '%'
					: `0%`
			};

			return (
				<Card
					className="key-graphics-single cursor-pointer"
					key={`key_${keyType}_${idx}`}
					onClick={() => handleRoute(item)}
				>
					<TopBar topText={idx === 0} />
					<PrintGraphicsImageContainer mainImage={mainImage} images={images} />
					<CardContent details={contents} tooltipSize={42} />
				</Card>
			);
		});
	};

	return (
		<LoaderComponent loading={isLoading}>
			<div className="key-graphics-section trend-padding scrollbar-on-hover">
				<div className="section-title pb-3 d-flex align-items-center justify-content-between">
					<p className="semibold-16 gray_dark_01 d-flex align-items-center">
						<CommonImage
							src={
								type === 'key-graphics'
									? '/icons/flag/key-grapics.svg'
									: '/icons/flag/key-prints.svg'
							}
							width={20}
						/>
						<span className="pl-1">{title}</span>
					</p>
				</div>
				{designs.length > 0 ? (
					<div className="graphics-slider trending-slider">{renderCards()}</div>
				) : (
					<EmptyState />
				)}
			</div>
		</LoaderComponent>
	);
};

KeyGraphicsOrPrint.propTypes = {
	title: string.isRequired,
	source: string.isRequired,
	summaryField: string.isRequired,
	keyType: string.isRequired
};

export default KeyGraphicsOrPrint;
